import {
  BasicTier,
  isUserTier,
  OAuthProvider,
  SubscriptionTier,
  TRACKING_LINK_PARAMS,
  UserTier,
  convertToAnalyticsString
} from 'notability-services-common-universal'

export type AnalyticsValue = number | boolean | string | Date | undefined

type LoginType = OAuthProvider | 'magic-link' | 'password'

// Facebook Standard Events - https://www.facebook.com/business/help/402791146561655?id=1205376682832142
type FacebookStandardEvent =
  | 'AddPaymentInfo'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'CompleteRegistration'
  | 'Contact'
  | 'CustomizeProduct'
  | 'Donate'
  | 'FindLocation'
  | 'InitiateCheckout'
  | 'Lead'
  | 'Purchase'
  | 'Schedule'
  | 'Search'
  | 'StartTrial'
  | 'SubmitApplication'
  | 'Subscribe'
  | 'ViewContent'

type FbqFunction = {
  (action: 'track', eventName: FacebookStandardEvent, params?: Record<string, any>): void
  (action: 'trackCustom', eventName: string, params?: Record<string, any>): void
}

export type MaybeHasFbqFunction = {fbq?: FbqFunction}

type RdtFunction = (action: 'track', eventName: string) => void
export type MaybeHasRdtFunction = {rdt?: RdtFunction}

type PodscribeInitOptions = {user_id: string; advertiser: string}
type PodscribePurchaseOptions = {
  value?: number
  order_number?: string
  hashed_email?: string
  currency?: string
  product?: string
  is_subscription?: boolean
}
type PodscribeFunction = {
  (action: 'init', options: PodscribeInitOptions): void
  (action: 'view'): void
  (action: 'purchase', options: PodscribePurchaseOptions): void
}
export type MaybeHasPodscribeFunction = {podscribe?: PodscribeFunction}

export const PODSCRIBE_USER_ID = 'f0281ca0-715d-4e71-8e2d-c81981ffdece'
export const PODSCRIBE_ADVERTISER = 'notability'
export const PODSCRIBE_TAG_URL = 'https://d34r8q7sht0t9k.cloudfront.net/tag.js'

export type LoginAnalyticsObj = {
  isNewAccount?: boolean
  type: LoginType
}

const loginAnalyticsKey = 'login_analytics'

export function setLoginAnalytics(loginAnalytics: LoginAnalyticsObj) {
  window.localStorage.setItem(loginAnalyticsKey, JSON.stringify(loginAnalytics))
}

export function getLoginAnalytics(): LoginAnalyticsObj | null {
  if (typeof window.localStorage === 'undefined') {
    return null
  }

  const loginAnalyticsStr = window.localStorage.getItem(loginAnalyticsKey)
  if (!loginAnalyticsStr) {
    return null
  }

  return JSON.parse(loginAnalyticsStr) as LoginAnalyticsObj
}

export function clearLoginAnalytics() {
  window.localStorage.removeItem(loginAnalyticsKey)
}

/**
 * Tiers whose internal name differs from the cross-platform Mixpanel vocabulary shared with iOS and
 * Android. Anything absent here already agrees once Title Cased. `lifetime` is a post-relaunch
 * one-time Classic purchase, and `legacy` the pre-subscription one; both read as Classic.
 */
const analyticsTierNames: Partial<Record<`${UserTier}`, string>> = {
  [SubscriptionTier.vpp]: 'VPP',
  [SubscriptionTier.premium]: 'Plus',
  [SubscriptionTier.light]: 'Lite',
  [SubscriptionTier.legacy]: 'Classic',
  [BasicTier.classic]: 'Classic',
  [BasicTier.lifetime]: 'Classic'
}

export function subscriptionTierToAnalyticsValue(tier: `${UserTier}`, isClassic: boolean = false) {
  if (isClassic) {
    return 'Classic'
  }
  return analyticsTierNames[tier] ?? convertToAnalyticsString(tier)
}

/**
 * The tier a user currently holds, for `Previous Subscription Tier` and the `Subscription Tier`
 * profile property. A user with no subscription has a null tier and is reported as Starter.
 */
export function heldTierToAnalyticsValue(tier: UserTier | null | undefined, isClassic: boolean = false) {
  return subscriptionTierToAnalyticsValue(tier ?? BasicTier.starter, isClassic)
}

/**
 * A tier that reached us as an untrusted string — from Stripe session metadata or a page prop.
 * Undefined when it isn't a tier we recognize, so the property is omitted rather than sent as
 * something a report can't group on.
 */
export function analyticsTierOrUndefined(tier: string | null | undefined): string | undefined {
  return isUserTier(tier) ? subscriptionTierToAnalyticsValue(tier) : undefined
}

// Re-exported from common-universal so existing importers from common-frontend continue to work.
export {TRACKING_LINK_PARAMS}

export const SINGULAR_TRACKING_LINK_PARAMS_KEY = 'singular_tracking_link_params'
export const NOTABILITY_AD_LANDING_URL_KEY = 'notability_ad_landing_url'
export const AD_PARAMS_STORED_AT_KEY = 'ad_params_stored_at'

const AD_PARAMS_EXPIRATION_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

export function getAdParamsFromLocalStorage(): Record<string, string> {
  const adTrackingProperties: Record<string, string> = {}
  const textEncoder = new TextEncoder()

  try {
    const storedAt = localStorage.getItem(AD_PARAMS_STORED_AT_KEY)
    if (storedAt) {
      const storedTime = new Date(storedAt).getTime()
      if (isNaN(storedTime) || Date.now() - storedTime > AD_PARAMS_EXPIRATION_MS) {
        return adTrackingProperties
      }
    }

    const storedTrackingParams = localStorage.getItem(SINGULAR_TRACKING_LINK_PARAMS_KEY)
    const adLandingUrl = localStorage.getItem(NOTABILITY_AD_LANDING_URL_KEY)

    if (storedTrackingParams) {
      try {
        const parsed = JSON.parse(storedTrackingParams)
        if (typeof parsed === 'object' && parsed !== null) {
          // Only include valid TRACKING_LINK_PARAMS
          TRACKING_LINK_PARAMS.forEach((param) => {
            if (parsed[param] && typeof parsed[param] === 'string') {
              // Sanitize and limit to Mixpanel's 255 byte limit https://docs.mixpanel.com/docs/data-structure/property-reference/data-type#string
              const value = String(parsed[param]).trim()
              if (value.length > 0 && textEncoder.encode(value).length <= 255) {
                adTrackingProperties[param] = value
              }
            }
          })
        }
      } catch (e) {
        console.warn('Failed to parse stored tracking params:', e)
      }
    }

    // Only add landing URL if we have tracking parameters (user came from ads)
    if (adLandingUrl && typeof adLandingUrl === 'string' && Object.keys(adTrackingProperties).length > 0) {
      const sanitizedUrl = String(adLandingUrl).trim()
      if (sanitizedUrl.length > 0 && textEncoder.encode(sanitizedUrl).length <= 255) {
        adTrackingProperties.adLandingUrl = sanitizedUrl
      }
    }
  } catch (e) {
    console.warn('Failed to access localStorage for ad tracking properties:', e)
  }

  return adTrackingProperties
}
